import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";

/** One object's rating state: the viewer's own score plus the public aggregate. */
export interface RatingEntry {
  /** The signed-in user's own score, or null if they have not rated this yet. */
  score: number | null;
  /** Average score across all users. */
  rating: number;
  /** Number of ratings submitted. */
  reviews: number;
  loading: boolean;
}

// Keyed by object id.
//
// This used to be a single `rating: {score, loading}` slot shared by the whole app.
// Every mounted card subscribes to this slice, so rating one card wrote its score
// into all of them, and two cards loading at once raced each other. Keying by object
// id makes each card's state its own.
export interface ReviewState {
  ratings: Record<string, RatingEntry>;
}

const initialState: ReviewState = {
  ratings: {},
};

export const EMPTY_RATING: RatingEntry = {
  score: null,
  rating: 0,
  reviews: 0,
  loading: false,
};

/** Read one object's rating state, falling back to a stable empty entry. */
export const selectRating = (
  state: { review: ReviewState },
  objectId: string
): RatingEntry => state.review.ratings[objectId] ?? EMPTY_RATING;

function entryFor(state: ReviewState, objectId: string): RatingEntry {
  if (!state.ratings[objectId]) {
    state.ratings[objectId] = { ...EMPTY_RATING };
  }
  return state.ratings[objectId];
}

/** Copy the aggregate off a response, ignoring a server that does not send it. */
function applyAggregate(entry: RatingEntry, data: any) {
  if (typeof data?.rating === "number") entry.rating = data.rating;
  if (typeof data?.reviews === "number") entry.reviews = data.reviews;
}

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get the current user's rating for a piece of content
export const getReviewForContent = createAsyncThunk(
  "review/content/get",
  async (
    obj: {
      contentId: string;
      contentType: string;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.id = obj.contentId;
      params.type = obj.contentType;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/review/content${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Create or Update review rating
export const submitReviewRating = createAsyncThunk(
  "review/content/create",
  async (
    obj: {
      objectId: string;
      objectType: string;
      score: number;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        }
      };

      // Build endpoint URL
      const url = `/api/review`;

      const body = JSON.stringify({
        id: obj.objectId,
        type: obj.objectType,
        score: obj.score,
      });

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      const data = err?.response?.data;
      return rejectWithValue(data?.error || data?.message || err?.message || "");
    }
  }
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const reviewSlice = createSlice({
  name: "review",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      .addCase(getReviewForContent.pending, (state, action) => {
        entryFor(state, action.meta.arg.contentId).loading = true;
      })
      .addCase(getReviewForContent.fulfilled, (state, action) => {
        const entry = entryFor(state, action.meta.arg.contentId);
        entry.loading = false;
        entry.score = action.payload.data.score;
        applyAggregate(entry, action.payload.data);
      })
      .addCase(getReviewForContent.rejected, (state, action) => {
        entryFor(state, action.meta.arg.contentId).loading = false;
      })

      .addCase(submitReviewRating.pending, (state, action) => {
        entryFor(state, action.meta.arg.objectId).loading = true;
      })
      .addCase(submitReviewRating.fulfilled, (state, action) => {
        // The POST returns the recomputed average and count, so the card can show
        // the new numbers immediately instead of waiting for the (cached) list
        // endpoint to catch up.
        const entry = entryFor(state, action.meta.arg.objectId);
        entry.loading = false;
        entry.score = action.payload.data.score;
        applyAggregate(entry, action.payload.data);
      })
      .addCase(submitReviewRating.rejected, (state, action) => {
        entryFor(state, action.meta.arg.objectId).loading = false;
      });

  },
});

export default reviewSlice.reducer;
